import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract, faShip } from "@fortawesome/pro-duotone-svg-icons";
import { AssignmentInd } from "@mui/icons-material";
import { DetailsBlock, Height } from "view/charters/dossier/Blocks";
import { Card } from "@cofano/cck";
import { RelationLink } from "utils/LinkUtils";
import { useQuery } from "@apollo/client";
import {
	CharterInvoiceGroupsDocument,
	CharterInvoiceGroupsQuery,
} from "__generated__/view/declarations/graphql/declaration.queries.generated";
import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray.tsx";
import { Skeleton, Typography } from "@mui/material";
import { DataFormat } from "utils";
import InvoiceLinesTable from "../../../declarations/InvoiceLinesTable";
import { useDeclarationActions } from "view/declarations/utils/useDeclarationActions";
import { CharterType } from "__generated__/enums";
import { intl } from "LocaleWrapper";
import AgreementsTable from "view/charters/dossier/contract/AgreementsTable";
import { ChildProps } from "types/commons";

type DeclarationBlockRelation = {
	id: number;
	name: string;
};

type DeclarationBlockProps = {
	contract: {
		charterType?: CharterType | null;
		isEditable?: boolean | null;
		creditor?: DeclarationBlockRelation | null;
		debtor?: DeclarationBlockRelation | null;
	};
	color?: string;
	charter: {
		id: number;
		type: CharterType;
		creditor?: DeclarationBlockRelation | null;
	};
	declarationId?: number;
	simple?: boolean;
	collapsable?: boolean;
};

type InvoiceGroupFromQuery = ChildProps<CharterInvoiceGroupsQuery, "charter">["invoiceGroups"][number];

const getMissingAgreements = (group: InvoiceGroupFromQuery) => {
	if (!group.declaration) {
		return [];
	}
	const declaredAgreementIds = new Set(
		group.declaration.invoiceLines.map(l => l?.agreement?.id).filter(Boolean),
	);
	return group.agreements.filter(a => !declaredAgreementIds.has(a.agreement.id));
};

const DeclarationBlock = ({ contract, color = "#F39200", charter, declarationId, simple, collapsable }: DeclarationBlockProps) => {
	const getCardInformation = () => {
		if (contract.charterType === CharterType.Purchase) {
			return {
				icon: <FontAwesomeIcon icon={faShip} style={{ color: color, height: 22, width: 22 }} />,
				relation: contract.creditor,
			};
		} else if (contract.charterType === CharterType.Sales) {
			return {
				icon: <AssignmentInd sx={{ color: color }} />,
				relation: contract.debtor,
			};
		}
		return null;
	};

	const cardInformation = getCardInformation();

	const {
		data,
		loading,
		refetch,
	} = useQuery(CharterInvoiceGroupsDocument, { variables: { charterId: charter.id, declarationId } });

	const groups = data?.charter?.invoiceGroups ?? [];

	const totalPriceExcVAT = groups
		.map(g => g.declaration?.totalPriceExcVAT ?? 0)
		.reduce((sum, v) => sum + v, 0) || null;

	// the group instance if all agreements have the same (or none) invoice group type
	const singleGroup = groups.length === 1 ? groups[0] : null;

	// invoiced relation
	const invoicedRelation = singleGroup ? (contract.charterType === CharterType.Purchase ? singleGroup.creditor : singleGroup.debtor) : null;

	const { cardActions, dialogs } = useDeclarationActions({
		declaration: singleGroup?.declaration ?? null,
		charter: singleGroup ? charter : undefined,
		invoiceGroupType: singleGroup?.invoiceGroupType,
		isEditable: contract.isEditable ?? true,
		simple,
		refetch,
	});

	return (
		<DetailsBlock height={Height.Auto}>
			<Card
				color={simple ? undefined : color}
				icon={cardInformation?.icon ?? <FontAwesomeIcon icon={faFileContract} style={{ color: color, height: 22, width: 22 }} />}
				title={<RelationLink relation={invoicedRelation ?? cardInformation?.relation} />}
				subtitle={DataFormat.price(totalPriceExcVAT ?? 0)}
				collapsable={collapsable}
				disablePadding
				actions={singleGroup ? cardActions : [ cardActions[0] ].filter(Boolean)}
			>
				{loading && (
					<div style={{ padding: 16 }}>
						{Array.from(Array(5)).map((_, index) => (
							<Skeleton
								variant="text"
								width="100%"
								height={50}
								style={{ margin: "0, 10" }}
								key={index}
							/>
						))}
					</div>
				)}

				<FormArrayContainer padding={groups.length > 1 ? undefined : 0}>
					{groups.length > 1 ? (
						groups.map(group => (
							<InvoiceGroupCard
								key={group.declaration?.id ?? `undeclared-${group.invoiceGroupType?.id ?? "none"}`}
								group={group}
								charter={charter}
								isEditable={contract.isEditable ?? true}
								simple={simple}
								refetch={refetch}
							/>
						))
					) : singleGroup?.declaration ? (
						<InvoiceLinesTable
							declaration={singleGroup.declaration}
							mutedAgreements={getMissingAgreements(singleGroup).map(it => it.agreement)}
							refetch={refetch}
						/>
					) : singleGroup ? (
						<AgreementsTable agreements={singleGroup.agreements} />
					) : null}
				</FormArrayContainer>
			</Card>
			{dialogs}
		</DetailsBlock>
	);
};

type InvoiceGroupCardProps = {
	group: InvoiceGroupFromQuery;
	charter: DeclarationBlockProps["charter"];
	isEditable?: boolean;
	simple?: boolean;
	refetch: () => void;
};

const InvoiceGroupCard = ({ group, charter, isEditable, simple, refetch }: InvoiceGroupCardProps) => {
	const declaration = group.declaration ?? null;

	const { cardActions, dialogs } = useDeclarationActions({
		declaration,
		charter,
		invoiceGroupType: group.invoiceGroupType,
		isEditable,
		simple,
		refetch,
	});

	const reroutedRelation = charter.type === CharterType.Purchase ? group.creditor : group.debtor;

	const title = <Typography textTransform="none"
		component="span"
		fontSize={13}
		fontWeight="bold">
		{group.invoiceGroupType?.name ?? intl.formatMessage({ id: "OTHER" })}
	</Typography>;

	const subtitle = <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
		{DataFormat.price(declaration?.totalPriceExcVAT ?? 0)}
		{reroutedRelation && (
			<>
				<span>-</span>
				<RelationLink relation={reroutedRelation} />
			</>
		)}
	</span>;

	return (
		<FormArrayRow
			title={title}
			subtitle={subtitle}
			onCustomActions={cardActions}
			disablePadding
		>
			{declaration && declaration.invoiceLines.length > 0 ? (
				<InvoiceLinesTable
					declaration={declaration}
					mutedAgreements={getMissingAgreements(group).map(it => it.agreement)}
					refetch={refetch}
				/>
			) : (
				<AgreementsTable agreements={group.agreements} />
			)}
			{dialogs}
		</FormArrayRow>
	);
};

export default DeclarationBlock;
